/**
 * Types du DAG d'inférence « solve-backward » (authoring admin).
 *
 * Miroir des structures renvoyées par la RPC `get_dag` (migration 0149) et de
 * l'entrée de l'edge fn `dag-build`. Le DAG est rattaché à une ligne `exercises`.
 */

export type DagIndiceType = 'donnee' | 'hypothese_implicite' | 'resultat_anterieur';

export interface DagIndice {
  id: string;
  span_debut: number;
  span_fin: number;
  type: DagIndiceType;
  valeur: string | null;
  question_origine: string | null;
}

export type DagSubnodeTrigger = 'on_error' | 'on_request' | 'always';

export interface DagNoeud {
  id: string;
  question_id: string | null;
  kind: 'dispatch' | 'inference';
  ordre: number;
  resultat: string;
  /** Outil canonique (id `objets_math`) pour un nœud inference. */
  outil_id: string | null;
  enonce_qcm: string | null;
  /** Chemin développé (migration 0169) : true = sous-nœud (hors DAG principal). */
  is_sub: boolean;
  /** Pour un sous-nœud : le nœud PRINCIPAL qu'il décompose (NULL si principal). */
  subnode_of: string | null;
  /** Sur un nœud PRINCIPAL : quand révéler son chemin développé. */
  subnode_trigger: DagSubnodeTrigger;
}

export interface DagArete {
  noeud_id: string;
  indice_id: string | null;
  parent_noeud_id: string | null;
}

export interface DagChoix {
  id: string;
  noeud_id: string;
  label: string;
  correct: boolean;
  misconception_id: string | null;
  ordre: number;
}

export interface DagBranche {
  choix_id: string;
  noeud_active_id: string;
}

export interface DagHeader {
  id: string;
  exercise_id: string;
  type_id: string;
  niveau: string;
  statut: string;
  corrige: string | null;
  updated_at: string;
}

export interface DagPayload {
  dag: DagHeader;
  indices: DagIndice[];
  noeuds: DagNoeud[];
  aretes: DagArete[];
  choix: DagChoix[];
  branches: DagBranche[];
  /** Indices qu'aucun nœud ne consomme = distracteurs d'énoncé. */
  indices_non_consommes: DagIndice[];
}

/** La RPC `get_dag` renvoie soit le DAG, soit `{ error }`. */
export type DagResult = DagPayload | { error: string };

export function isDagPayload(r: DagResult | null | undefined): r is DagPayload {
  return !!r && 'noeuds' in r;
}

/** Un type de problème du catalogue (pour le sélecteur d'authoring). */
export interface TypeDeProbleme {
  id: string;
  geste_id: string;
  objet_id: string;
  description: string;
  /** Niveau d'apparition de l'objet du type (filtrage par niveau). */
  niveau_apparition: string;
}

export interface BuildDagInput {
  exercise_id: string;
  type_id: string;
  niveau: string;
  enonce: string;
  corrige: string;
  mode?: 'strict' | 'reconstruct';
}

export interface BuildDagResult {
  ok: boolean;
  exercise_id: string;
  dag_id: string | null;
  noeud_count: number;
}
